import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/Auth";

import { Logo, Shapes } from "@/pages/Landing";

function googleLogin() {
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const redirectUrl = window.location.origin + "/";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

function GoogleBtn() {
  return (
    <button type="button" onClick={googleLogin} className="btn btn-ghost w-full" data-testid="google-login-btn">
      <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z"/><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/><path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z"/></svg>
      Continua con Google
    </button>
  );
}

function AuthFrame({ title, kicker, children }: any) {
  return (
    <div className="relative min-h-[100dvh] overflow-hidden">
      <Shapes />
      <div className="relative min-h-[100dvh] grid md:grid-cols-2 max-w-6xl mx-auto">
      <div className="relative hidden md:flex flex-col justify-between p-10 lg:p-14" data-testid="auth-side">
        <Logo />
        <div className="surface p-8 max-w-md space-y-4">
          <div className="text-3xl lg:text-4xl font-semibold tracking-tight leading-tight">Un passo alla volta.<br /><span className="font-serif-i text-[hsl(var(--primary))]">Insieme.</span></div>
          <p className="text-ink/70">Ascoltati, datti piccoli obiettivi e trova persone vere con cui condividere il percorso.</p>
        </div>
        <p className="text-xs text-ink/55">Diario e umore restano privati. Nessuna classifica.</p>
      </div>
      <div className="relative flex flex-col justify-center px-6 sm:px-12 py-12 max-w-lg w-full mx-auto">
        <div className="md:hidden mb-10"><Logo /></div>
        <div className="surface p-6 sm:p-8">
          {kicker && <div className="label text-[hsl(var(--primary))] mb-3">{kicker}</div>}
          <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight mb-8">{title}</h1>
          {children}
        </div>
      </div>
      </div>
    </div>
  );
}

export function Login() {
  const { setUser } = useAuth();
  const nav = useNavigate();
  const [f, setF] = useState({ email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/login", f);
      setUser(data);
      nav("/");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <AuthFrame title="Bentornato." kicker="Accedi">
      <form onSubmit={submit} className="space-y-4" data-testid="login-form">
        <input className="field" type="email" required placeholder="Email" autoComplete="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="login-email-input" />
        <input className="field" type="password" required placeholder="Password" autoComplete="current-password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} data-testid="login-password-input" />
        <button className="btn btn-primary w-full" disabled={busy} data-testid="login-submit-btn">{busy ? "Accesso…" : "Accedi"}</button>
        <div className="flex items-center gap-3 text-ink/45 text-xs"><span className="h-px flex-1 bg-white/45" />oppure<span className="h-px flex-1 bg-white/45" /></div>
        <GoogleBtn />
        <div className="flex justify-between text-sm text-ink/70 pt-2">
          <Link to="/password-dimenticata" className="hover:text-primary" data-testid="forgot-password-link">Password dimenticata?</Link>
          <Link to="/registrati" className="hover:text-primary" data-testid="goto-register-link">Crea account</Link>
        </div>
      </form>
    </AuthFrame>
  );
}

export function Register() {
  const { setUser } = useAuth();
  const nav = useNavigate();
  const [f, setF] = useState({ email: "", password: "", display_name: "", username: "", birthdate: "", terms: false });
  const [busy, setBusy] = useState(false);
  const [uname, setUname] = useState<any>(null);
  useEffect(() => {
    if (f.username.length < 3) { setUname(null); return; }
    const t = setTimeout(() => api.get("/auth/username-available", { params: { u: f.username } }).then((r) => setUname(r.data)).catch(() => setUname(null)), 400);
    return () => clearTimeout(t);
  }, [f.username]);
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!f.terms) return toast.error("Accetta i Termini e l'informativa privacy");
    setBusy(true);
    try {
      const { terms, ...body } = f;
      const { data } = await api.post("/auth/register", body);
      setUser(data);
      toast.success(data.verification_sent ? "Ti abbiamo inviato un'email per confermare l'indirizzo" : "Account creato. L'email di conferma non è partita: potrai richiederla dal profilo");
      nav("/onboarding");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  const set = (k: string) => (e: any) => setF({ ...f, [k]: k === "username" ? e.target.value.toLowerCase().replace(/[^a-z0-9_.]/g, "") : e.target.value });
  return (
    <AuthFrame title="Inizia il tuo percorso." kicker="Crea account">
      <form onSubmit={submit} className="space-y-4" data-testid="register-form">
        <input className="field" required maxLength={50} placeholder="Nome visualizzato" value={f.display_name} onChange={set("display_name")} data-testid="register-name-input" />
        <div>
          <div className="flex"><span className="field w-auto border-r-0 text-ink/56">@</span><input className="field" required minLength={3} maxLength={24} placeholder="username" value={f.username} onChange={set("username")} data-testid="register-username-input" /></div>
          {uname && <p className={`text-xs mt-1 ${uname.available ? "text-[hsl(var(--lime))] font-semibold" : "text-destructive"}`} data-testid="username-availability">{uname.available ? "Disponibile" : uname.detail}</p>}
        </div>
        <input className="field" type="email" required placeholder="Email" autoComplete="email" value={f.email} onChange={set("email")} data-testid="register-email-input" />
        <input className="field" type="password" required minLength={8} placeholder="Password (min. 8 caratteri)" autoComplete="new-password" value={f.password} onChange={set("password")} data-testid="register-password-input" />
        <label className="block">
          <span className="label text-ink/62">Data di nascita · minimo 16 anni</span>
          <input className="field mt-2 [color-scheme:light]" type="date" required value={f.birthdate} onChange={set("birthdate")} data-testid="register-birthdate-input" />
        </label>
        <label className="flex gap-3 items-start text-sm text-ink/76">
          <input type="checkbox" checked={f.terms} onChange={(e) => setF({ ...f, terms: e.target.checked })} className="mt-1 accent-[hsl(170_30%_27%)]" data-testid="register-terms-checkbox" />
          <span>Accetto i <Link to="/termini" className="text-primary">Termini</Link> e ho letto l'<Link to="/privacy" className="text-primary">informativa privacy</Link>.</span>
        </label>
        <button className="btn btn-primary w-full" disabled={busy} data-testid="register-submit-btn">{busy ? "Creazione…" : "Crea account"}</button>
        <GoogleBtn />
        <p className="text-sm text-ink/70">Hai già un account? <Link to="/accedi" className="text-primary" data-testid="goto-login-link">Accedi</Link></p>
      </form>
    </AuthFrame>
  );
}

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    try { await api.post("/auth/forgot-password", { email }); setSent(true); } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <AuthFrame title="Recupera l'accesso." kicker="Password dimenticata">
      {sent ? <p className="text-ink/76" data-testid="forgot-sent-msg">Se l'email è registrata, riceverai un link per scegliere una nuova password. Il link vale 1 ora.</p> : (
        <form onSubmit={submit} className="space-y-4" data-testid="forgot-form">
          <input className="field" type="email" required placeholder="La tua email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="forgot-email-input" />
          <button className="btn btn-primary w-full" data-testid="forgot-submit-btn">Invia link</button>
        </form>
      )}
      <Link to="/accedi" className="inline-block text-sm text-ink/70 mt-6 hover:text-primary" data-testid="back-to-login-link">← Torna all'accesso</Link>
    </AuthFrame>
  );
}

export function ResetPassword() {
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const [pwd, setPwd] = useState("");
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await api.post("/auth/reset-password", { token: sp.get("token"), password: pwd });
      toast.success("Password aggiornata. Accedi con quella nuova.");
      nav("/accedi");
    } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <AuthFrame title="Nuova password." kicker="Reimposta">
      <form onSubmit={submit} className="space-y-4" data-testid="reset-form">
        <input className="field" type="password" minLength={8} required placeholder="Nuova password (min. 8)" value={pwd} onChange={(e) => setPwd(e.target.value)} data-testid="reset-password-input" />
        <button className="btn btn-primary w-full" data-testid="reset-submit-btn">Salva password</button>
      </form>
    </AuthFrame>
  );
}

export function VerifyEmail() {
  const [sp] = useSearchParams();
  const { refresh } = useAuth();
  const [state, setState] = useState<"wait" | "ok" | "err">("wait");
  const [msg, setMsg] = useState("");
  const done = useRef(false);
  useEffect(() => {
    if (done.current) return;
    done.current = true;
    api.post("/auth/verify-email", { token: sp.get("token") }).then(() => { setState("ok"); refresh(); }).catch((e) => { setState("err"); setMsg(errMsg(e)); });
  }, [sp, refresh]);
  return (
    <AuthFrame title={state === "ok" ? "Email confermata." : state === "err" ? "Link non valido." : "Verifica in corso…"} kicker="Verifica email">
      <p className="text-ink/76 mb-6" data-testid="verify-status">{state === "ok" ? "Ora i tuoi voti contano nelle UNSEEN Daily (dopo 24 ore dall'iscrizione)." : msg}</p>
      <Link to="/" className="btn btn-primary" data-testid="verify-continue-btn">Continua</Link>
    </AuthFrame>
  );
}

export function AuthCallback() {
  const { setUser } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const done = useRef(false);
  useEffect(() => {
    if (done.current) return;
    done.current = true;
    const sid = new URLSearchParams(loc.hash.slice(1)).get("session_id");
    api.post("/auth/google/session", { session_id: sid })
      .then(({ data }) => { setUser(data); nav(data.onboarded ? "/" : "/onboarding", { replace: true }); })
      .catch((e) => { toast.error(errMsg(e)); setUser(false); nav("/accedi", { replace: true }); });
  }, [loc.hash, nav, setUser]);
  return <div className="min-h-[100dvh]" />;
}
